import React, { useCallback, useEffect, useReducer, useState } from "react"
import type { AtualizarTaxaPayload } from "../../../services/atribuirBairros"
import {
    adicionarBairrosDb,
    atualizarTaxaBairroDb,
    excluirBairroDb,
    obterBairrosDb,
} from "../../../services/atribuirBairros"
import {
    adicionarCidadeEstabelecimento,
    obterCidadesEstabelecimento,
    obterTodasCidades,
    removerCidadeEstabelecimento,
} from "../../../services/atribuirRegiao"
import { getIdEstabelecimentoAtual } from "../../../services/quitacaoAutomatica"
import useAppStore from "../../../stores/AppStore"
import useLoadingStore from "../../../stores/LoadingStore"
import { Loading } from "../../RegistradorDePedidos"
import type { BairroAtendido } from "../models/atribuirBairrosModel"
import { AtribuirBairrosInicial } from "../models/atribuirBairrosModel"
import { AtribuirBairrosActionsTypes } from "./atribuirBairrosActions"
import { AtribuirBairrosContext } from "./AtribuirBairrosContext"
import { atribuirBairrosReducer } from "./atribuirBairrosReducer"

export function AtribuirBairrosContextProvider({ children }: { children: React.ReactNode }) {
    const [dados, dispatch] = useReducer(atribuirBairrosReducer, AtribuirBairrosInicial)
    const [erroCarregamento, setErroCarregamento] = useState<string | null>(null)
    const setLoading = useLoadingStore(s => s.setLoading)
    const configuracoes = useAppStore(s => s.configuracoes)
    // Loja SELECIONADA no seletor (estabelecimentoAtualCF); parametros.estabelecimento
    // aponta para a loja do usuario logado e nao reflete a troca de loja.
    const idEstabelecimento =
        getIdEstabelecimentoAtual() || configuracoes?.user?.parametros?.estabelecimento?.id

    const carregarBairros = useCallback(
        async (idCidade: number | null) => {
            const autorizacao = localStorage.getItem("chaveSistema")
            if (!autorizacao || !idEstabelecimento) return

            setLoading(true)
            try {
                const res = await obterBairrosDb(autorizacao, idEstabelecimento, idCidade)
                if ("retornoErro" in res) {
                    setErroCarregamento(res.mensagem || "Erro ao carregar bairros")
                } else {
                    setErroCarregamento(null)
                    dispatch({
                        type: AtribuirBairrosActionsTypes.CARREGAR_DADOS,
                        payload: res,
                    })
                }
            } catch {
                setErroCarregamento("Erro ao carregar bairros")
            } finally {
                setLoading(false)
            }
        },
        [idEstabelecimento]
    )

    const carregar = useCallback(async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao || !idEstabelecimento) return

        setLoading(true)
        try {
            const [resCidades, resTodasCidades] = await Promise.all([
                obterCidadesEstabelecimento(autorizacao, idEstabelecimento),
                obterTodasCidades(autorizacao),
            ])

            let idCidadeInicial: number | null = null

            if (!("retornoErro" in resCidades)) {
                dispatch({
                    type: AtribuirBairrosActionsTypes.CARREGAR_CIDADES_ESTABELECIMENTO,
                    payload: resCidades,
                })
                if (resCidades.length > 0) {
                    idCidadeInicial = resCidades[0].id
                    dispatch({
                        type: AtribuirBairrosActionsTypes.SELECIONAR_CIDADE,
                        payload: idCidadeInicial,
                    })
                }
            }

            if (!("retornoErro" in resTodasCidades)) {
                dispatch({
                    type: AtribuirBairrosActionsTypes.CARREGAR_TODAS_CIDADES,
                    payload: resTodasCidades,
                })
            }

            const resBairros = await obterBairrosDb(autorizacao, idEstabelecimento, idCidadeInicial)
            if ("retornoErro" in resBairros) {
                setErroCarregamento(resBairros.mensagem || "Erro ao carregar bairros")
            } else {
                setErroCarregamento(null)
                dispatch({
                    type: AtribuirBairrosActionsTypes.CARREGAR_DADOS,
                    payload: resBairros,
                })
            }
        } catch {
            setErroCarregamento("Erro ao carregar bairros")
        } finally {
            setLoading(false)
        }
    }, [idEstabelecimento])

    useEffect(() => {
        carregar()
    }, [carregar])

    const adicionarBairros = useCallback(async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao || !idEstabelecimento || !dados.idCidadeSelecionada) return

        const bairrosSelecionados = dados.naoAtendidos
            .filter(b => dados.idsBairrosSelecionados.includes(b.id))
            .map(b => ({
                idBairro: b.id,
                taxaEntrega: dados.taxa.replace(",", "."),
                taxaEntregaNoturna: dados.taxaNoturna ? dados.taxaNoturna.replace(",", ".") : null,
                minimoEntregaGratis: dados.minimoEntregaGratis
                    ? dados.minimoEntregaGratis.replace(",", ".")
                    : null,
                taxaEntregador: dados.taxaEntregador
                    ? dados.taxaEntregador.replace(",", ".")
                    : null,
            }))

        if (bairrosSelecionados.length === 0) return

        setLoading(true)
        try {
            const response = await adicionarBairrosDb(
                autorizacao,
                idEstabelecimento,
                dados.idCidadeSelecionada,
                bairrosSelecionados
            )
            if ("retornoErro" in response) {
                throw new Error(response.mensagem)
            }
            dispatch({ type: AtribuirBairrosActionsTypes.CARREGAR_DADOS, payload: response })
        } finally {
            setLoading(false)
        }
    }, [dados, idEstabelecimento])

    const removerBairro = useCallback(async (id: number) => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return

        setLoading(true)
        try {
            const response = await excluirBairroDb(autorizacao, id)
            if (response && "retornoErro" in response) {
                throw new Error(response.mensagem || "Erro ao remover bairro")
            }
            dispatch({ type: AtribuirBairrosActionsTypes.REMOVER_BAIRRO_ATENDIDO, payload: id })
        } finally {
            setLoading(false)
        }
    }, [])

    const atualizarTaxa = useCallback(
        async (id: number, payload: AtualizarTaxaPayload): Promise<BairroAtendido> => {
            const autorizacao = localStorage.getItem("chaveSistema")
            if (!autorizacao) throw new Error("Não autenticado")

            setLoading(true)
            try {
                const response = await atualizarTaxaBairroDb(autorizacao, id, payload)
                if ("retornoErro" in response) {
                    throw new Error(response.mensagem || "Erro ao atualizar taxa")
                }
                dispatch({
                    type: AtribuirBairrosActionsTypes.ATUALIZAR_BAIRRO_ATENDIDO,
                    payload: response,
                })
                return response
            } finally {
                setLoading(false)
            }
        },
        []
    )

    const adicionarCidade = useCallback(
        async (idCidade: number) => {
            const autorizacao = localStorage.getItem("chaveSistema")
            if (!autorizacao || !idEstabelecimento) return

            setLoading(true)
            try {
                const response = await adicionarCidadeEstabelecimento(
                    autorizacao,
                    idEstabelecimento,
                    idCidade
                )
                if ("retornoErro" in response) {
                    throw new Error(response.mensagem || "Erro ao adicionar cidade")
                }
                dispatch({
                    type: AtribuirBairrosActionsTypes.CARREGAR_CIDADES_ESTABELECIMENTO,
                    payload: response,
                })
            } finally {
                setLoading(false)
            }
        },
        [idEstabelecimento]
    )

    const removerCidade = useCallback(
        async (idCidade: number) => {
            const autorizacao = localStorage.getItem("chaveSistema")
            if (!autorizacao || !idEstabelecimento) return

            setLoading(true)
            try {
                const response = await removerCidadeEstabelecimento(
                    autorizacao,
                    idEstabelecimento,
                    idCidade
                )
                if ("retornoErro" in response) {
                    throw new Error(response.mensagem || "Erro ao remover cidade")
                }
                dispatch({
                    type: AtribuirBairrosActionsTypes.CARREGAR_CIDADES_ESTABELECIMENTO,
                    payload: response,
                })
            } finally {
                setLoading(false)
            }
        },
        [idEstabelecimento]
    )

    return (
        <AtribuirBairrosContext.Provider
            value={{
                dados,
                dispatch,
                adicionarBairros,
                removerBairro,
                atualizarTaxa,
                adicionarCidade,
                removerCidade,
                carregarBairros,
                erroCarregamento,
            }}
        >
            <Loading />
            {children}
        </AtribuirBairrosContext.Provider>
    )
}
